import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mfaApi } from '../../services/enterpriseApi';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { LoadingScreen } from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { FieldLabel } from '../../components/common/Tooltip';
import {
  Shield,
  Smartphone,
  Mail,
  Key,
  Copy,
  CheckCircle,
  XCircle,
  AlertTriangle,
  QrCode,
} from 'lucide-react';

export default function MFAManagement() {
  const [totpSetupData, setTotpSetupData] = useState<any>(null);
  const [totpToken, setTotpToken] = useState('');
  const [smsPhone, setSmsPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'info'; message: string } | null>(null);
  const queryClient = useQueryClient();

  const notify = (type: 'success' | 'info', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  };

  const { data: mfaStatus, isLoading } = useQuery({
    queryKey: ['mfa-status'],
    queryFn: () => mfaApi.getStatus(),
  });

  const { data: backupCodes } = useQuery({
    queryKey: ['backup-codes'],
    queryFn: () => mfaApi.getBackupCodes(),
    enabled: showBackupCodes,
  });

  // TOTP Mutations
  const setupTOTPMutation = useMutation({
    mutationFn: () => mfaApi.setupTOTP(),
    onSuccess: (data) => {
      setTotpSetupData(data.data);
    },
  });

  const verifyTOTPMutation = useMutation({
    mutationFn: (token: string) => mfaApi.verifyTOTP({ token }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mfa-status'] });
      setTotpSetupData(null);
      setTotpToken('');
      notify('success', 'Authenticator app enabled successfully.');
    },
  });

  const disableTOTPMutation = useMutation({
    mutationFn: (token: string) => mfaApi.disableTOTP({ token }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mfa-status'] });
      setTotpToken('');
      notify('success', 'Authenticator app disabled.');
    },
  });

  // SMS Mutations
  const setupSMSMutation = useMutation({
    mutationFn: (phone: string) => mfaApi.setupSMS({ phone_number: phone }),
    onSuccess: () => {
      notify('info', 'Verification code sent to your phone.');
    },
  });

  const verifySMSMutation = useMutation({
    mutationFn: (code: string) => mfaApi.verifySMS({ code }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mfa-status'] });
      setSmsPhone('');
      setSmsCode('');
      notify('success', 'SMS MFA enabled successfully.');
    },
  });

  const disableSMSMutation = useMutation({
    mutationFn: () => mfaApi.disableSMS(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mfa-status'] });
      notify('success', 'SMS MFA disabled.');
    },
  });

  // Email Mutations
  const setupEmailMutation = useMutation({
    mutationFn: () => mfaApi.setupEmail(),
    onSuccess: () => {
      notify('info', 'Verification code sent to your email.');
    },
  });

  const verifyEmailMutation = useMutation({
    mutationFn: (code: string) => mfaApi.verifyEmail({ code }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mfa-status'] });
      setEmailCode('');
      notify('success', 'Email MFA enabled successfully.');
    },
  });

  const disableEmailMutation = useMutation({
    mutationFn: () => mfaApi.disableEmail(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mfa-status'] });
      notify('success', 'Email MFA disabled.');
    },
  });

  // Backup Codes Mutation
  const regenerateBackupCodesMutation = useMutation({
    mutationFn: () => mfaApi.regenerateBackupCodes(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-codes'] });
      notify('success', 'Backup codes regenerated successfully.');
    },
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    notify('info', 'Copied to clipboard.');
  };

  if (isLoading) {
    return <LoadingScreen message="Loading MFA settings..." />;
  }

  const status = mfaStatus?.data;

  return (
    <div className="space-y-6">
      {/* Inline notification banner */}
      {notification && (
        <Alert type={notification.type} onClose={() => setNotification(null)}>
          {notification.message}
        </Alert>
      )}

      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-gray-900 dark:text-white">
          Multi-Factor Authentication
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">
          Secure your account with additional authentication methods
        </p>
      </div>

      {/* MFA Status Overview */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Security Status</h2>
          </div>
          {status?.any_enabled ? (
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              MFA Enabled
            </span>
          ) : (
            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
              MFA Not Enabled
            </span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 dark:bg-metallic-800 rounded-lg border border-gray-200 dark:border-metallic-700">
            <Smartphone className={`w-8 h-8 mx-auto mb-2 ${status?.totp_enabled ? 'text-green-600' : 'text-gray-400 dark:text-gray-500'}`} />
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Authenticator App</p>
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
              {status?.totp_enabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-metallic-800 rounded-lg border border-gray-200 dark:border-metallic-700">
            <Smartphone className={`w-8 h-8 mx-auto mb-2 ${status?.sms_enabled ? 'text-green-600' : 'text-gray-400 dark:text-gray-500'}`} />
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">SMS</p>
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
              {status?.sms_enabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-metallic-800 rounded-lg border border-gray-200 dark:border-metallic-700">
            <Mail className={`w-8 h-8 mx-auto mb-2 ${status?.email_enabled ? 'text-green-600' : 'text-gray-400 dark:text-gray-500'}`} />
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Email</p>
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
              {status?.email_enabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>
        </div>
      </Card>

      {/* TOTP (Authenticator App) */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Smartphone className="w-5 h-5 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Authenticator App (TOTP)</h2>
        </div>

        {!status?.totp_enabled ? (
          <div className="space-y-4">
            {!totpSetupData ? (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Use an authenticator app like Google Authenticator, Authy, or 1Password to generate verification codes.
                </p>
                <Button
                  onClick={() => setupTOTPMutation.mutate()}
                  disabled={setupTOTPMutation.isPending}
                >
                  {setupTOTPMutation.isPending ? 'Setting up...' : 'Setup Authenticator App'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <Alert type="info" title="Scan QR Code">
                  Open your authenticator app and scan this QR code
                </Alert>

                {/* QR Code Display */}
                <div className="flex justify-center p-6 bg-white border-2 border-gray-200 rounded-lg">
                  {totpSetupData.qr_code ? (
                    <img src={totpSetupData.qr_code} alt="QR Code" className="w-48 h-48" />
                  ) : (
                    <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded">
                      <QrCode className="w-16 h-16 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Manual Entry */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Can't scan? Enter this code manually:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded font-mono text-sm">
                      {totpSetupData.secret}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(totpSetupData.secret)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Verification */}
                <div>
                  <FieldLabel
                    label="Enter 6-digit code from your app"
                    tooltip="Open your authenticator app (Google Authenticator, Authy, etc.) and enter the 6-digit code shown for Nabhos. The code refreshes every 30 seconds."
                    htmlFor="totp-verify-code"
                  />
                  <div className="flex gap-2">
                    <input
                      id="totp-verify-code"
                      type="text"
                      value={totpToken}
                      onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-center text-2xl font-mono"
                      maxLength={6}
                    />
                    <Button
                      onClick={() => verifyTOTPMutation.mutate(totpToken)}
                      disabled={totpToken.length !== 6 || verifyTOTPMutation.isPending}
                    >
                      {verifyTOTPMutation.isPending ? 'Verifying...' : 'Verify & Enable'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <Alert type="success" title="TOTP Enabled">
              Your authenticator app is set up and working
            </Alert>
            <div>
              <FieldLabel
                label="Enter code to disable"
                tooltip="To disable TOTP authentication, enter the current 6-digit code from your authenticator app. This will remove TOTP as a login requirement."
                htmlFor="totp-disable-code"
              />
              <div className="flex gap-2">
                <input
                  id="totp-disable-code"
                  type="text"
                  value={totpToken}
                  onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-center text-xl font-mono"
                  maxLength={6}
                />
                <Button
                  variant="outline"
                  onClick={() => disableTOTPMutation.mutate(totpToken)}
                  disabled={totpToken.length !== 6 || disableTOTPMutation.isPending}
                >
                  Disable
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* SMS MFA */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Smartphone className="w-5 h-5 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">SMS Verification</h2>
        </div>

        {!status?.sms_enabled ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Receive verification codes via SMS to your mobile phone
            </p>
            <div>
              <FieldLabel
                label="Phone Number (E.164 format)"
                tooltip="Enter your phone number in international format starting with + and country code (e.g., +1 for US, +91 for India). Example: +12025551234. SMS charges may apply."
                required
                htmlFor="sms-phone"
              />
              <div className="flex gap-2">
                <input
                  id="sms-phone"
                  type="tel"
                  value={smsPhone}
                  onChange={(e) => setSmsPhone(e.target.value)}
                  placeholder="+1234567890"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <Button
                  onClick={() => setupSMSMutation.mutate(smsPhone)}
                  disabled={!smsPhone || setupSMSMutation.isPending}
                >
                  {setupSMSMutation.isPending ? 'Sending...' : 'Send Code'}
                </Button>
              </div>
            </div>

            {setupSMSMutation.isSuccess && (
              <div>
                <FieldLabel
                  label="Enter verification code"
                  tooltip="Check your phone for a 6-digit SMS code. Enter it here within 10 minutes. If you don't receive it, check your phone number and try again."
                  htmlFor="sms-verify-code"
                />
                <div className="flex gap-2">
                  <input
                    id="sms-verify-code"
                    type="text"
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-center text-xl font-mono"
                    maxLength={6}
                  />
                  <Button
                    onClick={() => verifySMSMutation.mutate(smsCode)}
                    disabled={smsCode.length !== 6 || verifySMSMutation.isPending}
                  >
                    {verifySMSMutation.isPending ? 'Verifying...' : 'Verify & Enable'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <Alert type="success" title="SMS MFA Enabled">
              Verification codes will be sent to your registered phone number
            </Alert>
            <Button
              variant="outline"
              onClick={() => disableSMSMutation.mutate()}
              disabled={disableSMSMutation.isPending}
            >
              Disable SMS MFA
            </Button>
          </div>
        )}
      </Card>

      {/* Email MFA */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Email Verification</h2>
        </div>

        {!status?.email_enabled ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Receive verification codes via email to your registered email address
            </p>
            <Button
              onClick={() => setupEmailMutation.mutate()}
              disabled={setupEmailMutation.isPending}
            >
              {setupEmailMutation.isPending ? 'Sending...' : 'Send Verification Email'}
            </Button>

            {setupEmailMutation.isSuccess && (
              <div>
                <FieldLabel
                  label="Enter verification code from email"
                  tooltip="Check your registered email inbox for a 6-digit verification code. Also check spam/junk folders. The code expires in 10 minutes."
                  htmlFor="email-verify-code"
                />
                <div className="flex gap-2">
                  <input
                    id="email-verify-code"
                    type="text"
                    value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-center text-xl font-mono"
                    maxLength={6}
                  />
                  <Button
                    onClick={() => verifyEmailMutation.mutate(emailCode)}
                    disabled={emailCode.length !== 6 || verifyEmailMutation.isPending}
                  >
                    {verifyEmailMutation.isPending ? 'Verifying...' : 'Verify & Enable'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <Alert type="success" title="Email MFA Enabled">
              Verification codes will be sent to your registered email
            </Alert>
            <Button
              variant="outline"
              onClick={() => disableEmailMutation.mutate()}
              disabled={disableEmailMutation.isPending}
            >
              Disable Email MFA
            </Button>
          </div>
        )}
      </Card>

      {/* Backup Codes */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Key className="w-5 h-5 text-primary-600" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Backup Codes</h2>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Backup codes can be used to access your account if you lose access to your MFA devices.
          Each code can only be used once.
        </p>

        {!showBackupCodes ? (
          <Button onClick={() => setShowBackupCodes(true)}>View Backup Codes</Button>
        ) : (
          <div className="space-y-4">
            {backupCodes?.data?.codes && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {backupCodes.data.codes.map((code: string, index: number) => (
                    <div
                      key={index}
                      className="px-3 py-2 bg-white border border-gray-300 rounded font-mono text-sm text-center"
                    >
                      {code}
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(backupCodes.data.codes.join('\n'))}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All Codes
                </Button>
              </div>
            )}

            <Alert type="warning" title="Important">
              Store these codes in a safe place. You won't be able to see them again.
            </Alert>

            <Button
              variant="outline"
              onClick={() => regenerateBackupCodesMutation.mutate()}
              disabled={regenerateBackupCodesMutation.isPending}
            >
              Regenerate Backup Codes
            </Button>
          </div>
        )}
      </Card>

      {/* Security Recommendations */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Security Recommendations</h3>
        <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p>Enable at least two MFA methods for maximum security</p>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p>Store backup codes in a secure password manager</p>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p>Authenticator apps (TOTP) are more secure than SMS</p>
          </div>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p>Never share your verification codes with anyone</p>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Made with Bob
